import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

type ToastVariant = 'success' | 'danger' | 'info' | 'warn';

// At most this many toasts are visible at once. New ones evict the
// oldest so a burst of errors can't bury the entire page.
const MAX_VISIBLE_TOASTS = 5;

// Tier durations by tone — danger errors need time to read; success
// confirmations don't.
const DEFAULT_DURATION_MS: Record<ToastVariant, number> = {
  success: 4500,
  info: 6000,
  warn: 9000,
  danger: 10000,
};

interface Toast {
  id: number;
  variant: ToastVariant;
  title: string;
  description?: string;
  /** Milliseconds before auto-dismiss. 0 means sticky. Default depends on variant. */
  durationMs?: number;
}

interface ToastInput {
  variant?: ToastVariant;
  title: string;
  description?: string;
  durationMs?: number;
}

interface ToastContextValue {
  toast: (input: ToastInput) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (input: ToastInput) => {
      const id = ++idRef.current;
      const variant = input.variant ?? 'info';
      const next: Toast = {
        id,
        variant,
        title: input.title,
        description: input.description,
        durationMs: input.durationMs ?? DEFAULT_DURATION_MS[variant],
      };
      setToasts((prev) => {
        const combined = [...prev, next];
        return combined.length > MAX_VISIBLE_TOASTS
          ? combined.slice(-MAX_VISIBLE_TOASTS)
          : combined;
      });
    },
    []
  );

  const value: ToastContextValue = {
    toast,
    success: (title, description) =>
      toast({ variant: 'success', title, description }),
    error: (title, description) =>
      toast({ variant: 'danger', title, description }),
    info: (title, description) =>
      toast({ variant: 'info', title, description }),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <ToastViewport toasts={toasts} onDismiss={dismiss} />,
        document.body
      )}
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed bottom-4 right-4 z-[var(--z-toast)] flex flex-col items-end gap-3 sm:bottom-6 sm:right-6"
      style={{
        bottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))',
        paddingRight: 'env(safe-area-inset-right, 0)',
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  success:
    'border-l-[3px] border-[var(--color-success)] bg-[var(--color-surface)]',
  danger:
    'border-l-[3px] border-[var(--color-danger)] bg-[var(--color-surface)]',
  info:
    'border-l-[3px] border-[var(--color-primary)] bg-[var(--color-surface)]',
  warn:
    'border-l-[3px] border-[var(--color-warn)] bg-[var(--color-surface)]',
};

const VARIANT_DOT: Record<ToastVariant, string> = {
  success: 'bg-[var(--color-success)]',
  danger: 'bg-[var(--color-danger)]',
  info: 'bg-[var(--color-primary)]',
  warn: 'bg-[var(--color-warn)]',
};

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: number) => void;
}) {
  useEffect(() => {
    if (!toast.durationMs) return;
    const id = window.setTimeout(() => onDismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(id);
  }, [toast.durationMs, toast.id, onDismiss]);

  return (
    <div
      role={toast.variant === 'danger' ? 'alert' : 'status'}
      className={[
        'pointer-events-auto w-full max-w-[380px]',
        'rounded-[6px] border border-[var(--color-rule)]',
        'shadow-[0_8px_28px_-8px_rgba(25,54,97,0.18)]',
        'prism-enter',
        VARIANT_CLASSES[toast.variant],
      ].join(' ')}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <span
          aria-hidden="true"
          className={[
            'mt-1.5 h-[8px] w-[8px] shrink-0 rounded-[1px]',
            VARIANT_DOT[toast.variant],
          ].join(' ')}
        />
        <div className="min-w-0 flex-1">
          <p className="font-sans text-[14px] font-semibold leading-tight text-[var(--color-ink)]">
            {toast.title}
          </p>
          {toast.description && (
            <p className="mt-1 font-sans text-[13px] leading-relaxed text-[var(--color-ink-muted)]">
              {toast.description}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          aria-label="Dismiss notification"
          className="ml-2 -mr-1 -mt-0.5 flex h-7 w-7 cursor-pointer items-center justify-center rounded-[3px] text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-primary)]"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
